import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import {
  type CallAdapter,
  type CallAdapterMode,
  createCallAdapter,
  fetchGuestSession,
  guestSessionQueryOptions,
  joinErrorInfo,
  joinMeeting,
  nextRejoinAttempt,
  rejoinExhausted,
  useActiveSpeakerIds,
  useAudioBlocked,
  useCallDisconnectReason,
  useCallStatus,
  useLocalMedia,
  useParticipants,
  useRoomAccessStore,
  useSpeakerRank,
  useSpotlightId,
} from '@/entities/call'
import { MeetingRefs, meetingDetailQueryOptions } from '@/entities/meeting'
import { useSession } from '@/entities/session'
import { participantVolume, useParticipantVolumes } from '@/features/adjust-participant-volume'
import {
  appendChatEnvelope,
  sendChatMessage,
  useChatNotifications,
  useChatStore,
} from '@/features/call-chat'
import {
  useLobbyNotifications,
  useParticipantJoinNotifications,
} from '@/features/call-notifications'
import { useEndMeeting } from '@/features/end-meeting'
import { RecordingIndicator, useRecording } from '@/features/record-meeting'
import {
  BackgroundPicker,
  toBackgroundOption,
  useBackgroundSelection,
} from '@/features/set-background'
import {
  applyDevice,
  DevicePicker,
  useDeviceHotplug,
  useDeviceSelection,
  useLocalMediaFailureToast,
  useMediaDevices,
} from '@/features/setup-devices'
import {
  NoiseSuppressionBadge,
  NoiseSuppressionToggle,
  useNoiseFailureToast,
  useNoiseSuppression,
} from '@/features/suppress-noise'
import { runtimeConfig } from '@/shared/config'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Spinner } from '@/shared/ui/spinner'
import { CallSidebar } from '@/widgets/call-sidebar'
import { CallToolbar } from '@/widgets/call-toolbar'
import { CallAudio, VideoGrid } from '@/widgets/video-grid'

export const Route = createFileRoute('/room/$id')({
  head: () => ({ meta: [{ title: 'Звонок · STIT Телемост' }] }),
  component: RoomPage,
})

const mode: CallAdapterMode = runtimeConfig.API_MODE === 'mock' ? 'fake' : 'real'

function RoomPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const roomAccess = useRoomAccessStore((s) => s.roomAccess)
  const returnTo = useRoomAccessStore((s) => s.returnTo)
  const setRoomAccess = useRoomAccessStore((s) => s.setRoomAccess)
  const clearRoomAccess = useRoomAccessStore((s) => s.clearRoomAccess)
  // Была ли комната доступна: отличаем холодный заход по ссылке /room/<id>
  // (никогда не входил → ведём на pre-join) от выхода из звонка (→ returnTo).
  const hadAccessRef = useRef(false)

  const participants = useParticipants()
  const activeSpeakerIds = useActiveSpeakerIds()
  const spotlightId = useSpotlightId()
  const speakerRank = useSpeakerRank()
  const localMedia = useLocalMedia()
  const status = useCallStatus()
  const disconnectReason = useCallDisconnectReason()
  const audioBlocked = useAudioBlocked()

  const backgroundSelection = useBackgroundSelection((s) => s.selection)
  const noiseSuppression = useNoiseSuppression((s) => s.enabled)
  const noiseLevel = useNoiseSuppression((s) => s.level)
  const [showEffects, setShowEffects] = useState(false)
  // На десктопе (lg+) панель видна сразу; на узких — открывается по кнопке.
  const [showSidebar, setShowSidebar] = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches,
  )
  // Поворот планшета и ресайз окна пересекают брейкпоинт: без подписки мобильный
  // оверлей остаётся висеть на широком экране, а на узком панель занимает всё.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = (e: MediaQueryListEvent) => setShowSidebar(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Контур смотрящего — по сессии, а не по returnTo: returnTo отвечает на
  // «куда вернуть», а не на «кто смотрит», и записывал заказчика в сотрудники.
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const isCustomer = session?.kind === 'customer'
  const hasSession = session != null
  // Модерация (mute/kick/end) — только организатору (явный is_organizer из
  // detail), лобби — любому сотруднику. Карточку запрашиваем обоим контурам
  // сессии; гостю — нет: гостевой ручкой бэк её не отдаёт.
  const { data: detail } = useQuery({
    ...meetingDetailQueryOptions(roomAccess?.meetingId ?? ''),
    enabled: session != null && roomAccess != null,
  })
  const canModerate = detail?.isOrganizer === true
  // Лобби — любому сотруднику: пригласить гостя может каждый, впустить тоже.
  const canAdmit = isEmployee && detail != null
  // Гостевая ссылка доступна любому сотруднику — пригласить гостя может каждый.
  const guestUrl =
    isEmployee && detail?.guestLink
      ? detail.guestLink.startsWith('http')
        ? detail.guestLink
        : `${window.location.origin}/guest/${detail.guestLink}`
      : undefined

  const meetingId = roomAccess?.meetingId ?? ''
  // Аудио пишется автоматически (webhook participant_joined на бэке) — кнопки
  // «начать запись» нет, индикатор лишь честно отражает факт записи.
  // Поллинг состояния — только сотруднику: endpoint под cookie-сессией, гость
  // ловил бы 401 каждые N секунд (индикатор записи для гостя — в Backlog).
  const recording = useRecording(meetingId, isEmployee && roomAccess != null)
  const recordingActive = recording.data?.state === 'recording'
  useNoiseFailureToast()
  useLocalMediaFailureToast()
  // Уведомления по центру сверху + звук: вход участника, гость в лобби,
  // чужие сообщения чата (у чата — тумблер в самом окне чата).
  useParticipantJoinNotifications()
  useLobbyNotifications(meetingId, canAdmit && meetingId !== '')
  useChatNotifications()
  const endMeeting = useEndMeeting(meetingId)
  const [showEndConfirm, setShowEndConfirm] = useState(false)

  const adapterRef = useRef<CallAdapter | null>(null)
  // Поколение подключения: устаревший адаптер (StrictMode-переигровка эффекта)
  // не пишет в общий callStore и глушится, не затирая актуальный.
  const genRef = useRef(0)

  // Автовосстановление разорванного звонка. server_shutdown/unknown — это не
  // «встреча завершена», а истёкший room-JWT (resume после сна/смены сети) или
  // рестарт медиасервера: перезапрашиваем свежий доступ (сотрудник — join,
  // гость — cookie-сессия) и переподключаемся, вместо финального экрана.
  const [rejoin, setRejoin] = useState<'idle' | 'reconnecting' | 'failed' | 'meeting-over'>('idle')
  const rejoinAttemptsRef = useRef(0)
  // Момент входа в комнату: по длительности звонка решается, была ли попытка
  // удачной, — сам по себе `connected` этого не доказывает (см. entities/call/rejoin).
  const connectedAtRef = useRef(0)
  const recoverable = disconnectReason === 'server_shutdown' || disconnectReason === 'unknown'
  useEffect(() => {
    if (status !== 'connected') return
    connectedAtRef.current = Date.now()
    // setState — только асинхронно (правило set-state-in-effect): сбрасываем
    // «неудачу» прошлого цикла, чтобы следующий обрыв снова попал в rejoin.
    const timer = setTimeout(() => setRejoin('idle'), 0)
    return () => clearTimeout(timer)
  }, [status])
  useEffect(() => {
    // roomAccess сознательно не в deps: setRoomAccess свежим токеном не должен
    // перезапускать этот эффект (двойной цикл переподключения); триггер —
    // только смена статуса соединения (адаптер выставляет connecting сам).
    if (status !== 'disconnected' || !recoverable) return
    if (useRoomAccessStore.getState().roomAccess == null) return
    if (runtimeConfig.API_MODE === 'mock') return
    // Лёгкий backoff: сервер мог перезапускаться, мгновенный залп не поможет.
    const timer = setTimeout(
      () => {
        const connectedAt = connectedAtRef.current
        connectedAtRef.current = 0
        const attempt = nextRejoinAttempt(
          rejoinAttemptsRef.current,
          connectedAt === 0 ? null : Date.now() - connectedAt,
        )
        if (rejoinExhausted(attempt)) {
          setRejoin('failed')
          return
        }
        rejoinAttemptsRef.current = attempt
        setRejoin('reconnecting')
        void (async () => {
          try {
            // Обе сессии хаба перезаходят своей ручкой join; гость — cookie-сессией.
            if (hasSession) {
              // Замок могли повесить, пока мы переподключались: без room-JWT
              // комнаты нет — гасим доступ, и страница уводит на prejoin, где
              // живёт экран ожидания допуска.
              const res = await joinMeeting(id)
              if (!res.roomAccess) {
                clearRoomAccess()
                return
              }
              setRoomAccess(res.roomAccess, returnTo)
              return
            }
            const r = await fetchGuestSession()
            if (
              r.kind === 'active' &&
              r.session.meetingId === id &&
              r.session.state === 'joined' &&
              r.session.roomAccess
            ) {
              setRoomAccess(r.session.roomAccess, returnTo)
            } else if (r.kind === 'ended' || r.kind === 'cancelled') {
              setRejoin('meeting-over')
            } else {
              setRejoin('failed')
            }
          } catch (error) {
            const info = joinErrorInfo(error)
            setRejoin(
              info.kind === 'ended' || info.kind === 'cancelled' ? 'meeting-over' : 'failed',
            )
          }
        })()
      },
      1000 * (rejoinAttemptsRef.current + 1),
    )
    return () => clearTimeout(timer)
  }, [status, recoverable, hasSession, id, returnTo, setRoomAccess, clearRoomAccess])

  // Холодный заход (refresh вкладки / прямая ссылка) — сперва гостевой
  // контекст из cookie: гостя нельзя вести на /join (там SSO-guard → /login,
  // который гостю недоступен). Допущенный гость получает свежий room-JWT прямо
  // из ответа и остаётся в комнате.
  // enabled без hadAccessRef (ref в render запрещён): после выхода из звонка
  // возможен один лишний фоновый fetch до навигации — безвреден.
  const guestCtx = useQuery({
    ...guestSessionQueryOptions(),
    enabled: roomAccess == null,
  })
  useEffect(() => {
    if (roomAccess) {
      hadAccessRef.current = true
      return
    }
    // После выхода из звонка — обратно на returnTo.
    if (hadAccessRef.current) {
      void navigate({ to: returnTo })
      return
    }
    if (runtimeConfig.API_MODE === 'mock') {
      void navigate({ to: '/join/$id', params: { id } })
      return
    }
    const r = guestCtx.data
    if (r === undefined) return // выясняем, гость ли это (query ретраит сеть сам)
    if (r.kind === 'active' && r.session.meetingId === id) {
      if (r.session.state === 'joined' && r.session.roomAccess) {
        setRoomAccess(r.session.roomAccess, '/')
        return
      }
      // waiting/left — обратно в гостевой флоу (lobby или форма имени).
      void navigate({ to: '/guest/$token', params: { token: r.session.token }, replace: true })
      return
    }
    if (r.kind === 'ended' || r.kind === 'cancelled') {
      void navigate({ to: '/', replace: true })
      return
    }
    // Не гость — сотрудничий pre-join (его guard разберётся дальше).
    void navigate({ to: '/join/$id', params: { id } })
  }, [roomAccess, returnTo, id, navigate, guestCtx.data, setRoomAccess])

  useEffect(() => {
    if (!roomAccess) return
    genRef.current += 1
    const gen = genRef.current
    let adapter: CallAdapter | null = null
    let offData: (() => void) | null = null

    void createCallAdapter(mode)
      .then(async (a) => {
        if (genRef.current !== gen) {
          void a.disconnect()
          return
        }
        adapter = a
        adapterRef.current = a
        offData = a.onData((envelope, from) => appendChatEnvelope(envelope, from))
        const selection = useDeviceSelection.getState()
        // Устройства захвата уезжают в connect: комната поднимает микрофон и
        // камеру сразу на них, без второго захвата с рестартом трека.
        await a.connect({
          url: roomAccess.signaling,
          token: roomAccess.roomToken,
          audio: selection.micEnabled,
          video: selection.camEnabled,
          ...(selection.micId ? { audioDeviceId: selection.micId } : {}),
          ...(selection.camId ? { videoDeviceId: selection.camId } : {}),
        })
        if (genRef.current !== gen) {
          void a.disconnect()
          return
        }
        // Вывод capture-опцией не задаётся — он живёт на элементах;
        // без выбора звук и так идёт в системное устройство.
        const { outputId } = useDeviceSelection.getState()
        if (outputId) await applyDevice(a, 'audiooutput', outputId)
        // Персистентные выбор фона и шумодав применяем сразу после подключения.
        await a.setBackground(toBackgroundOption(useBackgroundSelection.getState().selection))
        await a.setNoiseSuppression(
          useNoiseSuppression.getState().enabled,
          useNoiseSuppression.getState().level,
        )
      })
      // Отказ подключения адаптер уже перевёл в disconnected/unknown — дальше его
      // разбирает цикл авто-переподключения выше. Здесь ловим, чтобы отказ не
      // всплыл необработанным промисом.
      .catch(() => {})

    return () => {
      offData?.()
      if (adapter) {
        void adapter.disconnect()
        adapterRef.current = null
      }
    }
  }, [roomAccess])

  // Переписка живёт, пока идёт эта встреча: чистится на уходе со страницы и на
  // смене встречи, но переподключение (свежий room-JWT) её не трогает — терять
  // историю из-за моргнувшей сети нельзя.
  // biome-ignore lint/correctness/useExhaustiveDependencies: id — не значение для эффекта, а граница его жизни: cleanup обязан сработать на смене встречи
  useEffect(() => {
    return () => useChatStore.getState().clear()
  }, [id])

  // Закрытие вкладки и «назад» во время живого звонка — частая случайность,
  // после которой человек возвращается через prejoin. Браузер переспросит, пока
  // соединение есть; после выхода кнопкой предупреждение снимается само.
  useEffect(() => {
    if (status !== 'connected') return
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [status])

  // Живое переключение фона из пикера; на первом рендере адаптера ещё нет —
  // тогда фон уже применён в connect-эффекте выше.
  useEffect(() => {
    void adapterRef.current?.setBackground(toBackgroundOption(backgroundSelection))
  }, [backgroundSelection])

  // Живое переключение шумодава и его уровня из диалога эффектов; на первом
  // рендере адаптера ещё нет — тогда состояние применено в connect-эффекте выше.
  useEffect(() => {
    void adapterRef.current?.setNoiseSuppression(noiseSuppression, noiseLevel)
  }, [noiseSuppression, noiseLevel])

  // Персональная громкость: применяется и к тем, кто вошёл позже, поэтому
  // ключом эффекта служит состав удалённых участников. Список внутри восстановлен
  // из строки-ключа — сам массив пересоздаётся на каждом событии SDK и гонял бы
  // эффект вхолостую.
  const participantVolumes = useParticipantVolumes((s) => s.volumes)
  const remoteIdsKey = participants
    .filter((p) => !p.isLocal)
    .map((p) => p.id)
    .join('\n')
  useEffect(() => {
    const adapter = adapterRef.current
    if (!adapter || remoteIdsKey === '') return
    for (const participantId of remoteIdsKey.split('\n')) {
      void adapter.setParticipantVolume(
        participantId,
        participantVolume(participantVolumes, participantId),
      )
    }
  }, [remoteIdsKey, participantVolumes])

  // Живое переключение устройств из диалога настроек и возврат на системное,
  // когда выбранное железо исчезло: пустой выбор — не «оставить как есть», а
  // «вернуться к системному». На первом рендере адаптера нет — стартовый выбор
  // применяет connect-эффект.
  useDeviceHotplug()
  const camId = useDeviceSelection((s) => s.camId)
  const micId = useDeviceSelection((s) => s.micId)
  const outputId = useDeviceSelection((s) => s.outputId)
  useEffect(() => {
    void applyDevice(adapterRef.current, 'videoinput', camId)
  }, [camId])
  useEffect(() => {
    void applyDevice(adapterRef.current, 'audioinput', micId)
  }, [micId])
  useEffect(() => {
    void applyDevice(adapterRef.current, 'audiooutput', outputId)
  }, [outputId])

  function toggleMic() {
    void adapterRef.current?.setMicEnabled(!localMedia.mic)
  }
  function toggleCam() {
    void adapterRef.current?.setCamEnabled(!localMedia.cam)
  }
  function switchCamera() {
    void adapterRef.current?.switchCamera()
  }
  function toggleScreen() {
    const a = adapterRef.current
    if (!a) return
    void (localMedia.screen ? a.stopScreenShare() : a.startScreenShare())
  }
  function leave() {
    clearRoomAccess()
    void navigate({ to: returnTo })
  }
  function sendChat(text: string) {
    const a = adapterRef.current
    if (a) void sendChatMessage(a, text)
  }
  function endForAll() {
    endMeeting.mutate(undefined, { onSuccess: leave })
  }

  // Холодный заход: пока выясняем гостевой контекст / резолвим редирект —
  // спиннер, не пустой экран.
  if (!roomAccess) {
    return (
      <section className="container-x flex justify-center px-6 py-24">
        <Spinner label="Возвращаем вас во встречу" />
      </section>
    )
  }

  // Комнату завершили удалённо (организатор/кик) — финальный экран с выходом.
  // Восстановимые обрывы (recoverable) финалом не считаются, пока авто-rejoin
  // не исчерпал попытки; «встреча закончилась во время обрыва» — room_finished.
  const rejoinGaveUp = rejoin === 'failed' || rejoin === 'meeting-over'
  const ended =
    status === 'disconnected' &&
    disconnectReason !== undefined &&
    disconnectReason !== 'client_initiated' &&
    (!recoverable || rejoinGaveUp || runtimeConfig.API_MODE === 'mock')
  if (ended) {
    return (
      <MeetingEndedScreen
        reason={rejoin === 'meeting-over' ? 'room_finished' : disconnectReason}
        onLeave={leave}
      />
    )
  }

  const reconnecting = status === 'disconnected' && recoverable
  const connecting = (status === 'connecting' && participants.length === 0) || reconnecting
  // Нативный реконнект LiveKit (ICE-restart, смена сети): картинка замирает, но
  // треки остаются — сетку не прячем, иначе моргание на ровном месте. Вместо
  // этого честная строка в шапке, чтобы замершее видео не выглядело поломкой.
  const linkRecovering = status === 'reconnecting'

  return (
    <>
      <CallAudio participants={participants} />
      <section className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-3 py-3 sm:gap-4 sm:px-6 sm:py-4">
        <div className="flex min-h-6 flex-wrap items-center gap-x-3 gap-y-2">
          {/* О чём разговор: название встречи и её привязка к хабу. Гостю
              карточка не приезжает — у него шапка остаётся индикаторной. */}
          {detail && (
            <>
              <span className="min-w-0 max-w-full truncate text-sm text-text-primary sm:max-w-xs">
                {detail.title}
              </span>
              <MeetingRefs meeting={detail} hideCompany={isCustomer} />
            </>
          )}
          <RecordingIndicator active={recordingActive} />
          <NoiseSuppressionBadge />
          {/* Браузер не дал воспроизвести звук: участники говорят, человек их
              не слышит. Кнопка — тот самый жест, которого не хватало. */}
          {audioBlocked && (
            <Button size="sm" onClick={() => void adapterRef.current?.unblockAudio()}>
              Включить звук
            </Button>
          )}
          {linkRecovering && (
            <span
              role="status"
              className="inline-flex items-center gap-2 rounded-pill border border-border-default px-3 py-1 font-mono text-text-secondary text-xs uppercase tracking-wide"
            >
              <span className="dot-pulse h-2 w-2 rounded-pill bg-accent" aria-hidden />
              Связь восстанавливается
            </span>
          )}
        </div>
        <div className="flex min-h-0 flex-1 gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            {connecting ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label={reconnecting ? 'Переподключение…' : 'Подключение к комнате'} />
              </div>
            ) : (
              <VideoGrid
                participants={participants}
                activeSpeakerIds={activeSpeakerIds}
                spotlightId={spotlightId}
                speakerRank={speakerRank}
              />
            )}
          </div>
          {showSidebar && (
            <>
              {/* На узких экранах — выезжающая панель поверх видео с затемнением. */}
              <button
                type="button"
                aria-label="Закрыть панель"
                onClick={() => setShowSidebar(false)}
                className="fixed inset-0 z-30 bg-black/50 lg:hidden"
              />
              {/* Мобила: полноэкранный slide-over; планшет: панель у правого края;
                  десктоп (lg+): статичная колонка. */}
              <aside className="fixed inset-y-0 right-0 z-40 w-full p-2 sm:w-[min(20rem,90vw)] sm:p-3 lg:static lg:z-auto lg:w-80 lg:shrink-0 lg:p-0">
                <CallSidebar
                  meetingId={roomAccess.meetingId}
                  canModerate={canModerate}
                  canAdmit={canAdmit}
                  locked={detail?.locked === true}
                  {...(guestUrl ? { guestUrl } : {})}
                  onSendChat={sendChat}
                  onClose={() => setShowSidebar(false)}
                />
              </aside>
            </>
          )}
        </div>
        <div className="flex justify-center">
          <CallToolbar
            micOn={localMedia.mic}
            camOn={localMedia.cam}
            screenOn={localMedia.screen}
            onToggleMic={toggleMic}
            onToggleCam={toggleCam}
            onToggleScreen={toggleScreen}
            onSwitchCamera={switchCamera}
            onBackground={() => setShowEffects(true)}
            onToggleSidebar={() => setShowSidebar((v) => !v)}
            sidebarOn={showSidebar}
            {...(canModerate ? { onEnd: () => setShowEndConfirm(true) } : {})}
            onLeave={leave}
          />
        </div>

        <Dialog open={showEffects} onClose={() => setShowEffects(false)} title="Настройки">
          <div className="flex flex-col gap-4">
            {/* Монтируется по открытию: enumerate-пробник не крутится всю дорогу. */}
            {showEffects && <CallDeviceSettings />}
            <BackgroundPicker />
            <NoiseSuppressionToggle />
          </div>
        </Dialog>

        <Dialog
          open={showEndConfirm}
          onClose={() => setShowEndConfirm(false)}
          title="Завершить встречу"
        >
          <p className="text-sm text-text-secondary">
            Завершить встречу для всех участников? Отменить это действие нельзя.
          </p>
          <div className="mt-4 flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setShowEndConfirm(false)}>
              Не сейчас
            </Button>
            <Button variant="danger" size="sm" disabled={endMeeting.isPending} onClick={endForAll}>
              Завершить
            </Button>
          </div>
        </Dialog>
      </section>
    </>
  )
}

function CallDeviceSettings() {
  const { cameras, microphones, speakers, error } = useMediaDevices()
  return (
    <div className="flex flex-col gap-2">
      {error && <p className="text-sm text-accent">{error}</p>}
      <DevicePicker
        cameras={cameras}
        microphones={microphones}
        speakers={speakers}
        showToggles={false}
      />
    </div>
  )
}

const ENDED_MESSAGE: Record<string, string> = {
  room_finished: 'Организатор завершил встречу.',
  participant_removed: 'Вас удалили из встречи.',
  server_shutdown: 'Соединение с сервером потеряно.',
  duplicate_identity: 'Вы подключились к встрече из другого места.',
}

function MeetingEndedScreen({ reason, onLeave }: { reason?: string; onLeave: () => void }) {
  return (
    <section className="container-x flex flex-col items-center justify-center gap-6 px-6 py-24 text-center">
      <div className="flex flex-col items-center gap-2">
        <h1 className="t-h2 text-text-primary">Встреча завершена</h1>
        <p className="text-text-secondary">
          {(reason && ENDED_MESSAGE[reason]) || 'Звонок завершён.'}
        </p>
      </div>
      <Button onClick={onLeave}>Вернуться</Button>
    </section>
  )
}
